// src/components/testingMode/TestingModePanelContent.tsx
// Host-injected panel content for Testing Mode (dev/staging only).

import React, { useState, useEffect } from 'react';
import { FlaskConical } from 'lucide-react';
import {
  useGetTestingModeOptionsQuery,
  useGetTestingModeStatusQuery,
  useApplyTestingModeOverrideMutation,
  useClearTestingModeOverrideMutation,
} from '../../api/centralTestingModeApi';
import { useAppSelector } from '../../store/store';
import { selectTokenWorkspaceId } from '../../store/slices/centralAuthSlice';
import { APP_ID } from '../../config/centralAuth';
import { CRM_ROLES, type CrmRoleKey } from '@levelup/crm-roles';
import type { TestingModePanelProps } from '@levelup/ui-shell';

// In-App Role dropdown shown when Central role === `member`. The CRM
// variant (Agent / Coordinator / Analyst) is a CRM-local overlay — we do
// NOT send it to Central as `capabilityPreset` because Central's staging
// does not have CRM-specific presets registered (submitting CRM UUIDs or
// short aliases returns `{ ok: false, error: "Invalid capability preset" }`).
// Instead, the variant choice is persisted in localStorage and overlaid
// by `useCurrentCrmRole` so the sidebar gates correctly while Central's
// override is applied with just `role: 'member'`.
const CRM_MEMBER_VARIANTS: ReadonlyArray<{
  key: CrmRoleKey;
  label: string;
  description: string;
}> = [
  { key: 'agent',       label: CRM_ROLES.agent.label,       description: CRM_ROLES.agent.shortDescription },
  { key: 'coordinator', label: CRM_ROLES.coordinator.label, description: CRM_ROLES.coordinator.shortDescription },
  { key: 'analyst',     label: CRM_ROLES.analyst.label,     description: CRM_ROLES.analyst.shortDescription },
];

// localStorage key read by `useCurrentCrmRole` to overlay the CRM variant
// on a `member` Testing Mode override. Cleared on override clear.
export const CRM_TESTING_VARIANT_STORAGE_KEY = 'levelup_crm_testing_member_variant';

export const TestingModePanelContent: React.FC<TestingModePanelProps> = ({
  isOpen,
  onClose,
}) => {
  const currentWorkspaceId = useAppSelector(selectTokenWorkspaceId);

  const { data: status, isLoading: statusLoading } = useGetTestingModeStatusQuery();
  const { data: options, isLoading: optionsLoading } = useGetTestingModeOptionsQuery();
  const [applyOverride, { isLoading: isApplying }] = useApplyTestingModeOverrideMutation();
  const [clearOverride, { isLoading: isClearing }] = useClearTestingModeOverrideMutation();

  // Form state. Role options (including labels and descriptions) come from
  // Central's `TestingModeOptions.roles` — not a local CRM list — so the
  // dropdown is driven end-to-end by Central per suite standard.
  const [workspaceId, setWorkspaceId] = useState('');
  const [appId, setAppId] = useState(APP_ID);
  const [role, setRole] = useState<string>('');
  const [capabilityPreset, setCapabilityPreset] = useState('');
  const [visibilityPreset, setVisibilityPreset] = useState('');
  const [memberType, setMemberType] = useState('');
  // In-App Role picker for member-core roles. Sourced from CRM's own
  // `@levelup/crm-roles` list (Agent / Coordinator / Analyst). The value
  // is persisted locally — Central does not receive it as `capabilityPreset`.
  const [crmMemberVariant, setCrmMemberVariant] = useState<CrmRoleKey>(() => {
    if (typeof window === 'undefined') return 'agent';
    const stored = window.localStorage.getItem(CRM_TESTING_VARIANT_STORAGE_KEY);
    return (stored === 'agent' || stored === 'coordinator' || stored === 'analyst')
      ? stored
      : 'agent';
  });
  const [applyError, setApplyError] = useState<string | null>(null);
  const isMemberRole = role === 'member';

  // Initialize form with current workspace
  useEffect(() => {
    if (currentWorkspaceId && !workspaceId) {
      setWorkspaceId(currentWorkspaceId);
    }
  }, [currentWorkspaceId, workspaceId]);

  // Default the role to the first value Central returned. If Central returns
  // no roles, the dropdown stays empty — we do not substitute a local list.
  useEffect(() => {
    if (!role && options?.roles && options.roles.length > 0) {
      setRole(options.roles[0].key);
    }
  }, [options?.roles, role]);

  if (!isOpen) return null;

  if (statusLoading || optionsLoading) {
    return (
      <div className="p-8 text-center">
        <div className="w-6 h-6 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-gray-500 mt-2">Loading testing mode...</p>
      </div>
    );
  }

  const handleApply = async () => {
    setApplyError(null);
    try {
      // The role value is a Central-provided key (from
      // `TestingModeOptions.roles[].key`). We submit it as-is to the Central
      // apply-override endpoint — Central round-trips the same contract.
      if (!role) return;
      await applyOverride({
        workspaceId,
        appId,
        role,
        // Only send `capabilityPreset` when the user explicitly picked one
        // from Central's own `capabilityPresets` list. Never auto-derive it
        // from the CRM member variant — Central's staging does not have
        // CRM-specific presets registered.
        capabilityPreset: capabilityPreset || undefined,
        visibilityPreset: visibilityPreset || undefined,
        memberType: memberType || undefined,
      }).unwrap();
      // Persist the CRM variant locally only after Central accepts the
      // override, so a failed apply doesn't leave a stale overlay driving
      // `useCurrentCrmRole`.
      if (typeof window !== 'undefined') {
        if (isMemberRole) {
          window.localStorage.setItem(CRM_TESTING_VARIANT_STORAGE_KEY, crmMemberVariant);
        } else {
          window.localStorage.removeItem(CRM_TESTING_VARIANT_STORAGE_KEY);
        }
      }
      onClose();
      // Refresh switch-context to get new effective permissions
      // Then hard reload to ensure all components re-render with new context
      window.location.reload();
    } catch (err) {
      console.error('Failed to apply testing mode:', err);
      const fallback = 'Failed to apply testing mode override.';
      if (err && typeof err === 'object' && 'data' in err) {
        const data = (err as { data?: { error?: string; message?: string } }).data;
        setApplyError(data?.error ?? data?.message ?? fallback);
      } else {
        setApplyError(fallback);
      }
    }
  };

  const handleClear = async () => {
    try {
      await clearOverride().unwrap();
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(CRM_TESTING_VARIANT_STORAGE_KEY);
      }
      onClose();
      // Refresh to restore actual permissions
      window.location.reload();
    } catch (err) {
      console.error('Failed to clear testing mode:', err);
    }
  };

  const isActive = status?.active ?? false;
  const currentOverride = status?.override;

  return (
    <div className="flex flex-col h-full max-h-[400px]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0 bg-amber-50">
        <h3 className="font-semibold text-amber-900 flex items-center gap-2">
          <FlaskConical className="w-5 h-5" />
          Testing Mode
        </h3>
        <p className="text-xs text-amber-700 mt-1">
          Simulate different roles and contexts (dev/staging only)
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Current Override Status */}
        {isActive && currentOverride && (
          <div className="bg-amber-100 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-800">Override Active</p>
            <div className="text-xs text-amber-700 mt-1 space-y-0.5">
              <p>App: {currentOverride.appName || currentOverride.appId}</p>
              <p>Role: {
                options?.roles.find((r) => r.key === currentOverride.role)?.label
                  ?? currentOverride.role
              }</p>
              <p>Workspace: {currentOverride.workspaceName || currentOverride.workspaceId}</p>
              {currentOverride.capabilityPreset && (
                <p>Capabilities: {currentOverride.capabilityPreset}</p>
              )}
            </div>
            <button
              onClick={handleClear}
              disabled={isClearing}
              className="mt-3 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            >
              {isClearing ? 'Clearing...' : 'Clear Override'}
            </button>
          </div>
        )}

        {/* Override Form */}
        <div className="space-y-3">
          {/* Workspace */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Workspace
            </label>
            <select
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Select workspace...</option>
              {options?.workspaces.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}{ws.type ? ` (${ws.type})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* App */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App
            </label>
            <select
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {options?.apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          {/* Role — sourced from Central's TestingModeOptions.roles. No
              CRM-local role list; Central is the single source of truth. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {(options?.roles ?? []).map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {options?.roles.find((r) => r.key === role)?.description}
            </p>
          </div>

          {/* In-App Role — appears only when Role is `member`. Sourced
              from CRM's `@levelup/crm-roles` package. Persisted locally
              and read by `useCurrentCrmRole` to gate the sidebar; NOT
              sent to Central as `capabilityPreset`. */}
          {isMemberRole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                In-App Role
              </label>
              <select
                value={crmMemberVariant}
                onChange={(e) => setCrmMemberVariant(e.target.value as CrmRoleKey)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {CRM_MEMBER_VARIANTS.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {CRM_MEMBER_VARIANTS.find((v) => v.key === crmMemberVariant)?.description}
              </p>
            </div>
          )}

          {/* Capability Preset (optional) — surfaces Central's raw
              registered presets. The CRM In-App Role picker above is a
              local overlay only; setting this value sends an explicit
              preset key to Central (must match Central's registry). */}
          {options?.capabilityPresets && options.capabilityPresets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capability Preset (optional)
              </label>
              <select
                value={capabilityPreset}
                onChange={(e) => setCapabilityPreset(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Use role defaults</option>
                {options.capabilityPresets.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Visibility Preset (optional) */}
          {options?.visibilityPresets && options.visibilityPresets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility Preset (optional)
              </label>
              <select
                value={visibilityPreset}
                onChange={(e) => setVisibilityPreset(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Use role defaults</option>
                {options.visibilityPresets.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Member Type (optional) */}
          {options?.memberTypes && options.memberTypes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Member Type (optional)
              </label>
              <select
                value={memberType}
                onChange={(e) => setMemberType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Default</option>
                {options.memberTypes.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {applyError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {applyError}
          </div>
        )}

        {/* Apply + Cancel row. The Cancel button clears any active Testing
            Mode override and is always visible in the footer while `isActive`
            so operators can turn it off without hunting for the "Override
            Active" block above (which is easy to miss on smaller panels
            and unreliable when `status.override` is transiently null). */}
        <div className="flex gap-2">
          {isActive && (
            <button
              type="button"
              onClick={handleClear}
              disabled={isClearing || isApplying}
              className="flex-1 px-4 py-2 bg-white border border-amber-600 text-amber-700 rounded-md hover:bg-amber-50 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="testing-mode-cancel"
              title="Turn off Testing Mode for this session"
            >
              {isClearing ? 'Cancelling…' : 'Cancel Testing Mode'}
            </button>
          )}
          <button
            type="button"
            onClick={handleApply}
            disabled={!workspaceId || !appId || !role || isApplying || isClearing}
            className={`${isActive ? 'flex-1' : 'w-full'} px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isApplying ? 'Applying...' : isActive ? 'Update Override' : 'Apply Override'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestingModePanelContent;
